import { getLoginAccessToken } from "../login.service";
import { readUsers } from "../readUsers.service";
import { authTokenStore } from "./tokenStore";

// NOTE: This module used to wrap the login handshake in a retry cascade
// (handleLoginTokenErrors + cleanUpExpiredSession) that, on certain auth errors,
// invalidated the stored session and re-attempted login. That existed to recover
// from the backend returning a stale / inactive token on a fresh login.
//
// The backend login is now deterministic — a successful password login always mints
// exactly one clean, activatable token (see app/api/auth.py `force_new_token=True`),
// so the cascade is no longer needed and has been removed. `readUsers` and
// `getLoginAccessToken` both authenticate with raw credentials (Basic / form data),
// not a stored token, so a stale token cannot poison them either. Steady-state
// (post-login) token refresh is still handled by the request.ts 401 interceptor.
//
// The exported wrappers are kept (same signatures) so call sites are unchanged; they
// are now thin pass-throughs and let genuine failures propagate to the login UI.

const persistTokensFromResponse = (response: {
  access_token?: string | null;
  refresh_token?: string | null;
  user_id?: number | string | null;
}) => {
  if (!response) {
    return;
  }

  authTokenStore.setTokens({
    accessToken: response.access_token ?? undefined,
    refreshToken: response.refresh_token ?? undefined,
    userId:
      typeof response.user_id === "number" || typeof response.user_id === "string"
        ? String(response.user_id)
        : undefined,
  });
};

export const safeReadUsers = async (email: string, password: string) => {
  return await readUsers(email, password);
};

export const safeGetLoginAccessToken = async (
  email: string,
  password: string
) => {
  const response = await getLoginAccessToken(email, password);
  persistTokensFromResponse(response);
  return response;
};
